import React, { useState, useEffect } from 'react';
import { FileText, Download, Search, Filter, Calendar, CreditCard, CheckCircle, XCircle } from 'lucide-react';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { ClientEmptyState } from '../../components/client/guidance';
import { TransactionSkeleton } from '../../components/shared/ClientPageSkeleton';

const ClientTransactions = () => {
 const [isLoading, setIsLoading] = useState(true);
 const [searchTerm, setSearchTerm] = useState('');
 const [filterStatus, setFilterStatus] = useState('all');

 const [transactions] = useState([
 {
 id: 1,
 type: 'Payment',
 description: 'Deposit for Wedding Photography - Photo Studio Pro',
 amount: 750.00,
 status: 'completed',
 date: '2024-12-15',
 transactionId: 'TXN-2024-001'
 },
 {
 id: 2,
 type: 'Payment',
 description: 'Platform Fee - Corporate Event Booking',
 amount: 100.00,
 status: 'completed',
 date: '2024-12-10',
 transactionId: 'TXN-2024-002'
 },
 {
 id: 3,
 type: 'Refund',
 description: 'Refund for Cancelled Booking - Event Capture Co',
 amount: -500.00,
 status: 'completed',
 date: '2024-11-25',
 transactionId: 'TXN-2024-003'
 },
 {
 id: 4,
 type: 'Payment',
 description: 'Final Payment - Wedding Photography',
 amount: 750.00,
 status: 'pending',
 date: '2024-12-20',
 transactionId: 'TXN-2024-004'
 }
 ]);

 const filteredTransactions = transactions.filter(transaction => {
 const matchesSearch = transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
 transaction.transactionId.toLowerCase().includes(searchTerm.toLowerCase());
 const matchesFilter = filterStatus === 'all' || transaction.status === filterStatus;
 return matchesSearch && matchesFilter;
 });

 const getStatusIcon = (status) => {
 switch (status) {
 case 'completed':
 return <CheckCircle className="w-3.5 h-3.5" />;
 case 'pending':
 return <XCircle className="w-3.5 h-3.5" />;
 case 'failed':
 return <XCircle className="w-3.5 h-3.5" />;
 default:
 return null;
 }
 };

 const getStatusColor = (status) => {
 switch (status) {
 case 'completed':
 return 'bg-emerald-50 text-emerald-800 border border-emerald-200/80';
 case 'pending':
 return 'bg-amber-50 text-amber-800 border border-amber-200/80';
 case 'failed':
 return 'bg-red-50 text-red-800 border border-red-200/80';
 default:
 return 'bg-gray-100 text-gray-800 border border-gray-200';
 }
 };

 useEffect(() => {
 // Simulate API call
 const timer = setTimeout(() => {
 setIsLoading(false);
 }, 1000);
 return () => clearTimeout(timer);
 }, []);

 const totalAmount = filteredTransactions.reduce((sum, t) => sum + t.amount, 0);
 const hasActiveFilters = searchTerm || filterStatus !== 'all';

 const layoutProps = {
 title: 'Transaction History',
 subtitle: 'See payments tied to your bookings and download receipts.',
 guideKey: 'transactions',
 };

 if (isLoading) {
 return (
 <ClientPageLayout {...layoutProps}>
 <div className="space-y-5">
 <div className="product-panel p-4 sm:p-5 animate-pulse">
 <div className="flex items-center justify-between">
 <div className="space-y-2">
 <div className="h-4 w-32 bg-gray-200 rounded"></div>
 <div className="h-8 w-16 bg-gray-200 rounded"></div>
 </div>
 <div className="space-y-2">
 <div className="h-4 w-32 bg-gray-200 rounded"></div>
 <div className="h-8 w-24 bg-gray-200 rounded"></div>
 </div>
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex flex-col sm:flex-row gap-3 mb-4">
 <div className="h-10 flex-1 bg-gray-200 rounded-md animate-pulse"></div>
 <div className="h-10 w-32 bg-gray-200 rounded-md animate-pulse"></div>
 </div>
 <TransactionSkeleton count={5} />
 </div>
 </div>
 </ClientPageLayout>
 );
 }

 return (
 <ClientPageLayout {...layoutProps}>
 <div className="space-y-5">
 {/* Summary Card */}
 <div className="product-panel p-4 sm:p-5">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <p className="text-xs font-medium text-ink-tertiary mb-1">Total transactions</p>
 <p className="text-2xl font-bold text-ink">{filteredTransactions.length}</p>
 </div>
 <div className="text-right sm:text-left sm:border-l sm:border-gray-200 sm:pl-4">
 <p className="text-xs font-medium text-ink-tertiary mb-1">Total amount</p>
 <p className="text-2xl font-bold text-ink">
 ${Math.abs(totalAmount).toFixed(2)}
 </p>
 </div>
 </div>
 </div>

 {/* Filters */}
 <div className="product-panel p-4 sm:p-5">
 <div className="flex flex-col sm:flex-row gap-3">
 <div className="relative flex-1">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
 <input
 type="text"
 placeholder="Search transactions..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="w-full pl-10 pr-4 py-2.5 border border-border bg-surface text-ink rounded-md focus:outline-none focus:ring-2 focus:ring-ink focus:border-ink"
 />
 </div>
 <div className="relative">
 <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
 <select
 value={filterStatus}
 onChange={(e) => setFilterStatus(e.target.value)}
 className="pl-10 pr-8 py-2.5 border border-border bg-surface text-ink rounded-md focus:outline-none focus:ring-2 focus:ring-ink focus:border-ink appearance-none min-w-[10rem]"
 >
 <option value="all">All Status</option>
 <option value="completed">Completed</option>
 <option value="pending">Pending</option>
 <option value="failed">Failed</option>
 </select>
 </div>
 </div>
 </div>

 {/* Transactions List */}
 {filteredTransactions.length === 0 ? (
 <ClientEmptyState
 icon={FileText}
 title="No transactions found"
 description={
 hasActiveFilters
 ? 'Try adjusting your search or filter criteria.'
 : 'Payments and refunds tied to your bookings will appear here.'
 }
 />
 ) : (
 <div className="space-y-3">
 {filteredTransactions.map((transaction) => (
 <article
 key={transaction.id}
 className="product-panel p-4 sm:p-5 hover:border-ink transition-colors"
 >
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
 <div className="flex items-start gap-3 flex-1 min-w-0">
 <div className="w-10 h-10 bg-surface-muted border border-border rounded-md flex items-center justify-center flex-shrink-0">
 <CreditCard className="w-5 h-5 text-ink" />
 </div>
 <div className="flex-1 min-w-0">
 <div className="flex flex-wrap items-center gap-2 mb-1">
 <h3 className="font-semibold text-base text-ink">
 {transaction.type}
 </h3>
 <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
 {getStatusIcon(transaction.status)}
 <span className="capitalize">{transaction.status}</span>
 </span>
 </div>
 <p className="text-sm text-ink-secondary mb-2 leading-relaxed">
 {transaction.description}
 </p>
 <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-tertiary">
 <div className="flex items-center">
 <Calendar className="w-3.5 h-3.5 mr-1 text-ink" />
 <span>{new Date(transaction.date).toLocaleDateString()}</span>
 </div>
 <span>ID: {transaction.transactionId}</span>
 </div>
 </div>
 </div>
 <div className="flex items-center gap-3 sm:ml-4">
 <div className="text-right">
 <p className={`text-lg font-bold ${ transaction.amount >= 0 ? 'text-ink' : 'text-red-600' }`}>
 {transaction.amount >= 0 ? '+' : ''}${Math.abs(transaction.amount).toFixed(2)}
 </p>
 </div>
 <button
 type="button"
 className="p-2 text-gray-400 hover:text-ink hover:bg-surface-muted transition-colors rounded-md"
 title="Download receipt"
 >
 <Download className="w-5 h-5" />
 </button>
 </div>
 </div>
 </article>
 ))}
 </div>
 )}
 </div>
 </ClientPageLayout>
 );
};

export default ClientTransactions;
